import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fmtMonth, fmtTons, fmtPct, niceScale, CHART_PRIMARY, CHART_ACCENT } from '../utils';
import { ChartEmptyState } from './ChartEmptyState';

const WIDTH = 700;
const HEIGHT = 320;
const MARGIN = { top: 16, right: 46, bottom: 34, left: 46 };
const PLOT_W = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_H = HEIGHT - MARGIN.top - MARGIN.bottom;
const ROWS = 4;

export function VolumeOtdChart({ data = [], locale }) {
  const { t } = useTranslation();
  const [hoverIndex, setHoverIndex] = useState(null);
  if (!data.length) return <ChartEmptyState />;

  const volumes = data.map((p) => p.volumeTons ?? 0);
  const otdValues = data.map((p) => p.otdPct).filter((v) => v != null);

  const volumeScale = niceScale(Math.max(...volumes), ROWS, 0);
  const otdMin = otdValues.length ? Math.max(0, Math.floor(Math.min(...otdValues) - 3)) : 0;
  const otdMax = 100;
  const otdTicks = Array.from({ length: ROWS + 1 }, (_, i) => Math.round(otdMin + (i * (otdMax - otdMin)) / ROWS));

  const band = PLOT_W / data.length;
  const barW = Math.min(band * 0.55, 64);

  const yForVolume = (v) => MARGIN.top + PLOT_H - (v / volumeScale.max) * PLOT_H;
  const yForOtd = (v) => MARGIN.top + PLOT_H - ((v - otdMin) / (otdMax - otdMin || 1)) * PLOT_H;
  const xForIndex = (i) => MARGIN.left + band * i + band / 2;

  const linePoints = data.map((p, i) => ({
    x: xForIndex(i),
    y: p.otdPct != null ? yForOtd(p.otdPct) : null,
    otdPct: p.otdPct,
  }));
  const path = linePoints
    .filter((p) => p.y != null)
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');

  return (
    <div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        height={HEIGHT}
        preserveAspectRatio="none"
        className="overflow-visible"
      >
        {/* Gridlines + axis ticks */}
        {volumeScale.ticks.map((tick, i) => {
          const y = yForVolume(tick);
          return (
            <g key={i}>
              <line
                x1={MARGIN.left}
                x2={WIDTH - MARGIN.right}
                y1={y}
                y2={y}
                stroke="#E2E8F0"
                strokeWidth={1}
                strokeDasharray="2 4"
                vectorEffect="non-scaling-stroke"
              />
              <text x={MARGIN.left - 10} y={y} textAnchor="end" dominantBaseline="middle" fontSize={12} fill="#64748B">
                {tick}
              </text>
              <text x={WIDTH - MARGIN.right + 10} y={y} textAnchor="start" dominantBaseline="middle" fontSize={12} fill="#64748B">
                {otdTicks[i]}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((p, i) => {
          const x = xForIndex(i) - barW / 2;
          const y = yForVolume(p.volumeTons ?? 0);
          const h = MARGIN.top + PLOT_H - y;
          const isDimmed = hoverIndex != null && hoverIndex !== i;
          return (
            <g key={p.month} onMouseEnter={() => setHoverIndex(i)} onMouseLeave={() => setHoverIndex(null)}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={Math.max(h, 2)}
                rx={4}
                fill={CHART_PRIMARY}
                opacity={isDimmed ? 0.4 : 0.9}
              />
              {hoverIndex === i && (
                <text x={x + barW / 2} y={y - 8} textAnchor="middle" fontSize={11} fontWeight={600} fill="#334155">
                  {fmtTons(p.volumeTons, locale)}
                </text>
              )}
            </g>
          );
        })}

        {/* OTD line */}
        <path d={path} fill="none" stroke={CHART_ACCENT} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        {(() => {
          const plottedPoints = linePoints.filter((p) => p.y != null);
          const lastPlotted = plottedPoints[plottedPoints.length - 1];
          return linePoints.map((p, i) => p.y != null && (
            <g key={i} onMouseEnter={() => setHoverIndex(i)} onMouseLeave={() => setHoverIndex(null)}>
              <circle cx={p.x} cy={p.y} r={5} fill="#fff" stroke={CHART_ACCENT} strokeWidth={2.5} vectorEffect="non-scaling-stroke" />
              {(hoverIndex === i || p === lastPlotted) && (
                <text x={p.x} y={p.y - 12} textAnchor="middle" fontSize={11} fontWeight={600} fill={CHART_ACCENT}>
                  {fmtPct(p.otdPct, 0)}
                </text>
              )}
            </g>
          ));
        })()}

        {/* X-axis month labels */}
        {data.map((p, i) => (
          <text
            key={p.month}
            x={xForIndex(i)}
            y={HEIGHT - MARGIN.bottom + 22}
            textAnchor="middle"
            fontSize={12}
            fill="#64748B"
            className="capitalize"
          >
            {fmtMonth(p.month, locale)}
          </text>
        ))}
      </svg>

      <div className="flex items-center justify-center gap-5 mt-2 text-[11.5px] text-slate-600">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: CHART_PRIMARY }} />
          {t('reporting.legend_volume')}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_ACCENT }} />
          {t('reporting.legend_otd')}
        </div>
      </div>
    </div>
  );
}

export default VolumeOtdChart;
